import type { ApiPublicLocations } from 'src/types/location';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { READ_TIMEOUT_MS } from 'src/utils/requestError';

import { QueryKeys } from './QueryKeys';
import { REFERENCE_STALE_TIME } from './staleness';

/**
 * `GET /public/locations` — the countries and cities a dealer can be in (api#298, partners-spec
 * §3.8), for the two dropdowns of the dealer registration.
 *
 * Anonymous: the registration page has no session yet, and the list is reference data. Cached like
 * the other reference lists: it changes when the platform adds a city, not with a page view. Bounded
 * like the other reads a screen waits on: a request that neither answers nor fails would otherwise
 * hold the form on "Loading…" for as long as the browser cares to wait.
 *
 * `enabled` lets the registration ask for it only on the dealer plan — a buyer has no region to
 * choose (decision no. 13), so their page makes no request.
 */
export const useLocations = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: QueryKeys.locations(),
    queryFn: async (): Promise<ApiPublicLocations> => {
      const response = await axios.get<ApiPublicLocations>('/public/locations', { timeout: READ_TIMEOUT_MS });

      return response.data;
    },
    staleTime: REFERENCE_STALE_TIME,
  });
